<template>
  <v-footer class="footer-section ">
    <v-container>
      <v-row>
        <!-- First Column: Company Logo and Description -->
        <v-col cols="12" md="3">
          <v-img
              src="/images/white-logo.png"
              alt="Fuhai Tonda International Machine Logo"
              width="160"
              class="mb-4"
              contain
          />
          <h2 class="company-name mb-3">{{ t('brand.fullName') }}</h2>
          <p class="company-description">
            {{ t('brand.description') }}
          </p>
          
          <!-- Social Media Links -->
          <div class="social-media-section mt-4">
            <h4 class="social-title mb-3">{{ t('footer.followUs', 'Follow Us') }}</h4>
            <div class="social-icons">
              <a 
                href="https://facebook.com/fuhaitonda" 
                target="_blank" 
                rel="noopener noreferrer"
                class="social-btn facebook"
                :title="t('social.facebook', 'Facebook')"
              >
                <v-icon size="20">mdi-facebook</v-icon>
              </a>
              <a 
                href="https://linkedin.com/company/fuhaitonda" 
                target="_blank" 
                rel="noopener noreferrer"
                class="social-btn linkedin"
                :title="t('social.linkedin', 'LinkedIn')"
              >
                <v-icon size="20">mdi-linkedin</v-icon>
              </a>
              <a 
                href="https://instagram.com/fuhaitonda" 
                target="_blank" 
                rel="noopener noreferrer"
                class="social-btn instagram"
                :title="t('social.instagram', 'Instagram')"
              >
                <v-icon size="20">mdi-instagram</v-icon>
              </a>
              <a 
                href="https://twitter.com/fuhaitonda" 
                target="_blank" 
                rel="noopener noreferrer"
                class="social-btn twitter"
                :title="t('social.twitter', 'Twitter')"
              >
                <v-icon size="20">mdi-twitter</v-icon>
              </a>
            </div>
          </div>
        </v-col>

        <!-- Second Column: Contact Info -->
        <v-col cols="12" md="4">
          <h3 class="footer-title text-uppercase">{{ safeT('nav.contact', 'Contact Us') }}</h3>
          <ul class="footer-links pa-0">
            <li class="contact-item">
              <v-icon size="20" class="contact-icon">mdi-office-building-marker</v-icon>
              <span class="contact-text">{{ safeT('brand.addressLabel', 'Address: ') }}{{ safeT('brand.address', 'Room 210, Building 1, Shahu Science and Technology Park, No.183, Tinglan Lane, Suzhou Industrial Park, P.R.China') }}</span>
            </li>
            <li class="contact-item">
              <v-icon size="20" class="contact-icon">mdi-factory</v-icon>
              <span class="contact-text">{{ safeT('brand.productionBaseLabel', 'Production Base: ') }}{{ safeT('brand.productionBase', 'Fangcheng Sub-district, Fangzi District, High-tech Zone, Weifang City, Shandong Province, P.R.China') }}</span>
            </li>
            <li class="contact-item">
              <v-icon size="20" class="contact-icon">mdi-phone</v-icon>
              <a :href="'tel:' + safeT('brand.phone', '+86 15206211582')" class="contact-text">
                {{ safeT('brand.phone', '+86 15206211582') }}
              </a>
            </li>
            <li class="contact-item">
              <v-icon size="20" class="contact-icon">mdi-phone</v-icon>
              <a href="tel:+8613563489528" class="contact-text">
                +86 13563489528
              </a>
            </li>
            <li class="contact-item">
              <v-icon size="20" class="contact-icon">mdi-email</v-icon>
              <a :href="'mailto:' + getEmailAddress()" class="contact-text">
                {{ getEmailAddress() }}
              </a>
            </li>
          </ul>
        </v-col>

        <!-- Third Column: Navigation (Site Map) -->
        <v-col cols="12" md="2">
          <h3 class="footer-title text-uppercase">{{ t('nav.navigation') }}</h3>
          <ul class="footer-links pa-0">
            <li v-for="item in menuItems" :key="item.title">
              <router-link :to="item.path" class="footer-link">
                {{ t(`nav.${item.title}`) }}
              </router-link>
            </li>
          </ul>
        </v-col>

        <!-- Fourth Column: Inquiry Form -->
        <v-col cols="12" md="3">
          <h3 class="footer-title text-uppercase">{{ t('footer.inquiry') }}</h3>
          <v-form ref="footerFormRef" @submit.prevent="submitInquiry">
            <v-text-field
                v-model="inquiryForm.name"
                :label="t('footer.inquiryForm.name')"
                variant="outlined"
                density="compact"
                class="mb-2 form-field"
                hide-details
                bg-color="rgba(255, 255, 255, 0.1)"
                color="white"
                theme="dark"
                :rules="nameRules"
            ></v-text-field>
            <v-text-field
                v-model="inquiryForm.phone"
                :label="t('footer.inquiryForm.tel')"
                variant="outlined"
                density="compact"
                class="mb-2 form-field"
                hide-details
                bg-color="rgba(255, 255, 255, 0.1)"
                color="white"
                theme="dark"
                :rules="phoneRules"
            ></v-text-field>
            <v-text-field
                v-model="inquiryForm.email"
                :label="t('footer.inquiryForm.email')"
                variant="outlined"
                density="compact"
                class="mb-2 form-field"
                hide-details
                bg-color="rgba(255, 255, 255, 0.1)"
                color="white"
                theme="dark"
                :rules="emailRules"
            ></v-text-field>
            <v-textarea
                v-model="inquiryForm.message"
                :label="t('footer.inquiryForm.message')"
                variant="outlined"
                density="compact"
                class="mb-2 form-field"
                hide-details
                bg-color="rgba(255, 255, 255, 0.1)"
                color="white"
                theme="dark"
            ></v-textarea>
            <v-btn
                type="submit"
                variant="elevated"
                block
                class="mt-2 send-btn"
                color="white"
                :loading="submitting"
            >
              {{ t('footer.inquiryForm.sendMessage') }}
            </v-btn>
          </v-form>
          <v-snackbar v-model="snackbar" :timeout="2500" :color="snackbarColor" location="top">
            {{ snackbarText }}
          </v-snackbar>
        </v-col>
      </v-row>


    </v-container>
    <div  class="copy-right">

      <div class="copyright-section text-center">
        <span class="text-caption ">{{ t('copyright') }}</span>
      </div>
    </div>
  </v-footer>
</template>

<script setup>
import {useI18n} from 'vue-i18n'
import {ref} from 'vue'

const {t, locale} = useI18n({
  inheritLocale: true,
  useScope: 'global'
})

const menuItems = [
  {title: 'home', path: '/', icon: 'mdi-home'},
  {title: 'about', path: '/about', icon: 'mdi-information'},
  {title: 'products', path: '/products', icon: 'mdi-package-variant-closed'},
  {title: 'faq', path: '/faq', icon: 'mdi-help-circle-outline'},
  {title: 'contact', path: '/contact', icon: 'mdi-email'},
]

const inquiryForm = ref({
  name: '',
  phone: '',
  email: '',
  message: ''
})

const footerFormRef = ref()
const required = (v) => (!!v && String(v).trim().length > 0) || t('common.required') || 'Required'
const emailFormat = (v) => /.+@.+\..+/.test(String(v)) || t('common.invalidEmail') || 'Invalid email'
const nameRules = [required]
const phoneRules = [required]
const emailRules = [required, emailFormat]

const submitInquiry = async () => {
  submitting.value = true
  try {
    // 先校验
    if (footerFormRef.value?.validate) {
      const result = await footerFormRef.value.validate()
      if (result && result.valid === false) {
        submitting.value = false
        return
      }
    }
    
    // 调用接口提交数据
    const response = await fetch(submitUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: inquiryForm.value.name,
        email: inquiryForm.value.email,
        message: inquiryForm.value.message,
        phone: inquiryForm.value.phone,
        product_name: ''
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    console.log('提交结果:', data)
    
    // 检查业务状态码
    if (data.errCode === 0) {
      // 成功提示
      console.log('✅ 成功！')
      console.log('留言ID:', data.data?.id)
      console.log('地理位置:', data.data?.location)
      
      snackbarColor.value = 'success'
      snackbarText.value = t('footer.inquiryForm.success') || '提交成功'
      snackbar.value = true
      
      // 重置表单
      if (footerFormRef.value?.reset) {
        footerFormRef.value.reset()
      } else {
        inquiryForm.value = {
          name: '',
          phone: '',
          email: '',
          message: ''
        }
        if (footerFormRef.value?.resetValidation) {
          footerFormRef.value.resetValidation()
        }
      }
    } else {
      // 业务失败
      console.log('❌ 失败:', data.errMsg)
      snackbarColor.value = 'error'
      snackbarText.value = data.errMsg || t('footer.inquiryForm.error') || '提交失败，请稍后重试'
      snackbar.value = true
    }
  } catch (error) {
    console.error('Form submission error:', error)
    // 错误提示
    snackbarColor.value = 'error'
    snackbarText.value = t('footer.inquiryForm.error') || '提交失败，请稍后重试'
    snackbar.value = true
  } finally {
    submitting.value = false
  }
}

const snackbar = ref(false)
const snackbarText = ref('Success')
const snackbarColor = ref('success')
const submitting = ref(false)

const submitUrl = "https://fhtd.tsanyi.top/api/submit"

// 安全的翻译获取函数，带fallback
const safeT = (key, fallback) => {
  try {
    const result = t(key)
    // 检查是否返回了翻译键本身（表示翻译失败）
    if (result && result !== key && !result.startsWith(key.split('.')[0] + '.')) {
      return result
    }
  } catch (error) {
    console.warn('Translation error for key:', key, error)
  }
  return fallback
}

// 获取邮箱地址的专门函数，避免i18n编译问题
const getEmailAddress = () => {
  try {
    const result = t('brand.email')
    if (result && result !== 'brand.email' && !result.startsWith('brand.')) {
      return result
    }
  } catch (error) {
    console.warn('Translation error for brand.email:', error)
  }
  // 使用安全的fallback方式
  return 'sarah' + String.fromCharCode(64) + 'davol.com'
}
</script>

<style scoped>
.footer-section {
  /*background: linear-gradient(to bottom, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));*/
  display: block;
  background: #2D3748;
  color: rgba(255, 255, 255, 0.9);
  padding:  46px 0 0;
}

/* 修复表单字段的样式 */
.form-field {
  --v-theme-surface: rgba(255, 255, 255, 0.1);
}

:deep(.v-field) {
  border-color: rgba(255, 255, 255, 0.3) !important;
}

:deep(.v-field__outline) {
  opacity: 0.5;
}

:deep(.v-field__field) {
  color: white !important;
}

:deep(.v-field__field) input {
  color: white !important;
}

:deep(.v-field__field) textarea {
  color: white !important;
}

:deep(.v-label) {
  color: rgba(255, 255, 255, 0.9) !important;
  opacity: 1;
}

.send-btn {
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.company-name {
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.company-description {
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  font-size: 1rem;
}

.footer-text {
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  font-size: 1rem;
}

.social-media-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1.5rem;
}

.social-title {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.social-icons {
  display: flex;
  gap: 12px;
  align-items: center;
}

.social-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.social-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.social-btn.facebook {
  background: #1877f2;
}

.social-btn.facebook:hover {
  background: #166fe5;
}

.social-btn.linkedin {
  background: #0a66c2;
}

.social-btn.linkedin:hover {
  background: #004182;
}

.social-btn.instagram {
  background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
}

.social-btn.instagram:hover {
  background: linear-gradient(45deg, #e8832a 0%, #dc5d33 25%, #d01e3a 50%, #c01a5d 75%, #b0157f 100%);
}

.social-btn.twitter {
  background: #1da1f2;
}

.social-btn.twitter:hover {
  background: #0d8bd9;
}

.social-btn .v-icon {
  color: white !important;
}

.footer-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  letter-spacing: 1px;
  color: white;
}

.footer-links {
  list-style: none;
}

.footer-links li {
  margin-bottom: 1rem;
}

.footer-link {
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  font-size: 1rem;
  display: inline-block;
  padding: 2px 0;
  text-decoration: none;
}

.footer-link:hover {
  color: white;
  transform: translateX(4px);
}

.contact-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.2rem;
}

.contact-icon {
  margin-right: 12px;
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.7);
}

.contact-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  line-height: 1.5;
  text-decoration: none;
}

a.contact-text:hover {
  color: white;
  text-decoration: none;
}


.copy-right{
  background-color: rgb(var(--v-theme-primary));
  padding: 20px 0;
}

.copyright-section {
  color: white;
  background-color: rgb(var(--v-theme-primary));
}

@media (max-width: 960px) {
  .footer-title {
    margin-bottom: 1.2rem;
    margin-top: 1.5rem;
    font-size: 1rem;
  }

  .footer-links li {
    margin-bottom: 0.8rem;
  }

  .contact-item {
    margin-bottom: 1rem;
  }

  .footer-link,
  .contact-text {
    font-size: 0.95rem;
  }
}

@media (max-width: 600px) {
  .footer-section {
    padding-top: 3rem !important;
  }

  .social-media-section {
    margin-top: 2rem;
    padding-top: 1rem;
  }

  .social-icons {
    margin-bottom: 2rem;
    justify-content: center;
  }

  .social-title {
    font-size: 0.95rem;
    text-align: center;
  }

  .footer-title {
    font-size: 0.95rem;
    margin-bottom: 1rem;
  }

  .footer-link,
  .footer-text,
  .contact-text {
    font-size: 0.9rem;
  }

  .social-btn {
    width: 36px;
    height: 36px;
  }
}
</style>